import { inject, Injectable, signal, NgZone } from '@angular/core';
import { getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  browserLocalPersistence,
  getAuth,
  getRedirectResult,
  isSignInWithEmailLink,
  linkWithCredential,
  linkWithPopup,
  linkWithRedirect,
  onAuthStateChanged,
  setPersistence,
  signInAnonymously,
  signInWithCredential,
  signInWithEmailLink,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type Auth,
  type AuthCredential,
  type User,
  initializeAuth,
  indexedDBLocalPersistence,
} from 'firebase/auth';
import { ReCaptchaEnterpriseProvider, getToken as getAppCheckToken, initializeAppCheck, type AppCheck } from 'firebase/app-check';
import { getFirestore, initializeFirestore, type Firestore } from 'firebase/firestore';
import { timeFirstAppCheckToken } from '../utils/native-boot';
import { GoogleSignIn } from '@capawesome/capacitor-google-sign-in';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';

import { APPCHECK_SITE_KEY, APP_BUNDLE_ID, FIREBASE_CONFIG, FUNCTIONS_BASE, GOOGLE_WEB_CLIENT_ID, IS_NATIVE } from '../env';
import { DiagService, describeError } from './diag.service';
import { ToastController } from '@ionic/angular';

const EMAIL_LINK_KEY = 'av.emaillink.addr';
const REDIRECT_KEY = 'av.redirect-auth';
/** Local caches cleared on sign-out so accounts never bleed into each other. */
const LOCAL_CACHE_KEYS = ['av.garage.v1', 'av.profile.v1', 'av.carctx.v1'];

/**
 * Anonymous-first auth with account upgrade (pattern ported from TalentHub365,
 * adapted for anonymous data): visitors get a silent uid immediately; signing
 * in with Google or an email link LINKS the provider onto the anonymous user,
 * so the uid — and every garage car, scan and badge under it — survives.
 * If the provider account already exists elsewhere, we sign into it instead
 * and the local-first caches merge this device's items into it on next sync.
 *
 * iOS Safari / PWA quirk handling (from TalentHub): popups hang in standalone
 * mode and are unreliable in iOS Safari, so those paths use redirect auth;
 * completePendingSignIn() finishes the round-trip on bootstrap.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  /** Public so analytics can attach to the same initialised app. */
  readonly app = getApps().length ? getApps()[0] : initializeApp(FIREBASE_CONFIG);
  // Capacitor iOS: getAuth()'s defaults (browser popup/redirect resolver +
  // browserLocal persistence) wedge inside the WKWebView - every sign-in's
  // REST call succeeds (users appear in the console) but the SDK never
  // settles locally, so currentUser stays null, no token is ever sent and
  // the account sheet never flips. Build 8-10 field tests. Firebase's own
  // Capacitor guidance: initializeAuth with indexedDBLocalPersistence and no
  // resolver on native. Web keeps getAuth() (popup/redirect flows need it).
  readonly auth: Auth = IS_NATIVE ? initializeAuth(this.app, { persistence: indexedDBLocalPersistence }) : getAuth(this.app);
  // WKWebView buffers Firestore's WebChannel stream, so first reads on the installed app
  // can stall ~10 s (house doc §6, "Firestore transport"). Native only; the web keeps the
  // SDK's own transport detection.
  readonly db: Firestore = nativeFirestore(this.app);
  private appCheck: AppCheck | null = null;

  readonly uid = signal<string | null>(null);
  readonly email = signal<string | null>(null);
  readonly isAnonymous = signal(true);
  /** An email link landed but we don't know the address (opened on another device). */
  readonly pendingEmailLink = signal(false);
  /** Address a sign-in link was just sent to ('' = none). */
  readonly emailLinkSentTo = signal('');
  readonly authBusy = signal(false);
  readonly authError = signal('');

  /** Resolves once the first auth state (incl. anonymous sign-in) settles. */
  readonly ready: Promise<void>;

  constructor() {
    this.ready = new Promise<void>((resolve) => {
      let settled = false;
      const settle = () => {
        if (!settled) {
          settled = true;
          resolve();
        }
      };
      // Cold visits emit null FIRST (before the anonymous sign-in lands), so
      // ready must wait for an actual user — settling on the first emission
      // made early gated calls go out tokenless and 401 as "sign in first"
      // (caught by the plate-extras E2E clicking Dealer valuation ~3s in).
      // The timer is the escape hatch when sign-in genuinely fails: better a
      // clean 401 path than an app hung on a promise forever.
      const failsafe = setTimeout(settle, 8000);
      onAuthStateChanged(this.auth, (user) => {
        this.uid.set(user?.uid ?? null);
        this.email.set(user?.email ?? null);
        this.isAnonymous.set(user?.isAnonymous ?? true);
        if (user) {
          clearTimeout(failsafe);
          settle();
        }
      });
    });
    try {
      this.appCheck = initializeAppCheck(this.app, {
        provider: new ReCaptchaEnterpriseProvider(APPCHECK_SITE_KEY),
        isTokenAutoRefreshEnabled: true,
      });
      // reCAPTCHA cannot attest inside the native shell; time it for the native.boot row.
      const appCheck = this.appCheck;
      if (IS_NATIVE) timeFirstAppCheckToken(() => getAppCheckToken(appCheck));
    } catch (e) {
      console.warn('App Check init failed:', (e as Error).message);
      this.diag.report('app check init failed', describeError(e));
    }
    void this.bootstrap();
  }

  /**
   * Firebase quirk: linking a provider onto an anonymous user does NOT refire
   * onAuthStateChanged, so signals (and the whole account UI) go stale — the
   * "signed in with Google but still see the sign-in pitch" bug. Reload the
   * user and recompute; providerData is the source of truth for anonymity.
   */
  /** Minimal inline event write (AnalyticsService depends on this service). */
  private logEvent(name: string, props: Record<string, string> = {}): void {
    void (async () => {
      try {
        const { Timestamp, addDoc, collection, serverTimestamp } = await import('firebase/firestore');
        const { IS_PROD } = await import('../env');
        const uid = this.uid();
        if (!uid) return;
        await addDoc(collection(this.db, 'events'), { name, at: serverTimestamp(), uid, app: 'car', env: IS_PROD ? 'prod' : 'dev', props, expireAt: Timestamp.fromMillis(Date.now() + 180 * 86_400_000) });
      } catch {
        /* never throws */
      }
    })();
  }

  private async refreshUserSignals(): Promise<void> {
    const u = this.auth.currentUser;
    if (!u) return;
    await u.reload().catch(() => undefined);
    const fresh = this.auth.currentUser;
    if (!fresh) return;
    // Native plugin promises (Apple/Google sheets) can settle outside Angular's
    // zone; write the signals inside it so the account sheet re-renders at once.
    this.zone.run(() => {
      this.uid.set(fresh.uid);
      this.email.set(fresh.email ?? fresh.providerData[0]?.email ?? null);
      this.isAnonymous.set(fresh.isAnonymous && fresh.providerData.length === 0);
    });
  }

  /** True when the browser refused persistent storage (private windows):
   *  the session — and everything under an anonymous uid — dies on refresh. */
  readonly ephemeralSession = signal(false);
  readonly diag = inject(DiagService);
  private readonly zone = inject(NgZone);
  private readonly toastCtrl = inject(ToastController);
  /** 'pending' | 'ok' | 'failed' - whether the device session (anonymous sign-in) landed. */
  readonly bootstrapState = signal<'pending' | 'ok' | 'failed'>('pending');
  readonly bootstrapError = signal('');

  private async bootstrap(): Promise<void> {
    try {
      if (!IS_NATIVE) {
        try {
          await setPersistence(this.auth, browserLocalPersistence);
          if (!storageWorks()) this.ephemeralSession.set(true);
        } catch {
          this.ephemeralSession.set(true);
        }
      }
      // Native shells never return from a redirect and never open email links,
      // and getRedirectResult can hang on the capacitor:// scheme - which
      // would starve the anonymous sign-in below. Skip it there; time-box it
      // on the web so a stuck resolver can never block the device account.
      const completed = IS_NATIVE ? false : await withTimeout(this.completePendingSignIn(), 6000, false);
      // The stored session loads asynchronously; deciding before it has landed
      // minted a NEW device account over a signed-in one on every launch
      // (build 15 field test: 'closed the app, reopened, not logged in').
      await this.auth.authStateReady();
      if (!completed && !this.auth.currentUser) {
        await signInAnonymously(this.auth);
      }
      this.bootstrapState.set('ok');
      this.diag.setUid(this.auth.currentUser?.uid ?? null);
    } catch (e) {
      const info = describeError(e);
      this.bootstrapState.set('failed');
      this.bootstrapError.set(`${info.code || info.name || 'error'}: ${info.message}`);
      console.warn('Auth bootstrap:', info.message);
      this.diag.report('auth bootstrap failed', info, 'error');
    }
  }

  // ─────────────────────────── Google ───────────────────────────

  async signInWithGoogle(): Promise<void> {
    this.authBusy.set(true);
    this.authError.set('');
    try {
      if (IS_NATIVE) {
        // Native webviews can't run signInWithPopup — use the platform's own
        // Google sheet, then hand the ID token to Firebase (TalentHub pattern).
        await this.ensureNativeGoogleInit();
        const native = await GoogleSignIn.signIn();
        const idToken = native.idToken?.trim();
        if (!idToken) throw new Error('Google sign-in did not return an ID token.');
        await this.linkOrSignInNative(GoogleAuthProvider.credential(idToken), 'google');
        return;
      }
      const provider = new GoogleAuthProvider();
      const current = this.auth.currentUser;
      if (this.prefersRedirectAuth()) {
        try {
          sessionStorage.setItem(REDIRECT_KEY, 'google');
        } catch {
          /* ITP */
        }
        if (current?.isAnonymous) await linkWithRedirect(current, provider);
        else await signInWithRedirect(this.auth, provider);
        return; // completes via completePendingSignIn on return
      }
      if (current?.isAnonymous) {
        try {
          await linkWithPopup(current, provider); // uid (and all data) survives
        } catch (e) {
          await this.recoverProviderConflict(e, provider);
        }
      } else {
        await signInWithPopup(this.auth, provider);
      }
      await this.refreshUserSignals();
      this.logEvent('signin', { method: 'google' });
    } catch (e) {
      this.authError.set(friendlyAuthError(e));
      this.diag.report(isCancelledSignIn(e) ? 'sign-in cancelled' : 'sign-in failed', describeError(e));
    } finally {
      this.authBusy.set(false);
    }
  }

  /**
   * linkWithPopup fails with credential-already-in-use when this Google
   * account already owns a uid. Switch to that account; this device's
   * local caches merge into it on the next sync pass.
   */
  private async recoverProviderConflict(e: unknown, provider: GoogleAuthProvider): Promise<void> {
    const err = e as { code?: string };
    if (err.code === 'auth/credential-already-in-use' || err.code === 'auth/email-already-in-use') {
      const cred = GoogleAuthProvider.credentialFromError(e as Parameters<typeof GoogleAuthProvider.credentialFromError>[0]);
      if (cred) {
        await signInWithCredential(this.auth, cred);
        return;
      }
      await signInWithPopup(this.auth, provider);
      return;
    }
    throw e;
  }

  // ─────────────────────────── Native (Capacitor) sign-in ───────────────────────────

  private googleInit: Promise<void> | null = null;
  private ensureNativeGoogleInit(): Promise<void> {
    if (!this.googleInit) this.googleInit = GoogleSignIn.initialize({ clientId: GOOGLE_WEB_CLIENT_ID });
    return this.googleInit;
  }

  /**
   * Sign in with Apple — iOS native only (Apple mandates it wherever Google
   * sign-in is offered). Same anonymous-upgrade contract as every other
   * provider: link onto the current uid, existing account wins on conflict.
   */
  async signInWithApple(): Promise<void> {
    this.authBusy.set(true);
    this.authError.set('');
    try {
      await this.linkOrSignInNative(await this.appleCredential(), 'apple', () => this.appleCredential());
    } catch (e) {
      this.authError.set(friendlyAuthError(e));
      this.diag.report(isCancelledSignIn(e) ? 'sign-in cancelled' : 'sign-in failed', describeError(e));
    } finally {
      this.authBusy.set(false);
    }
  }

  /** The last Apple token + raw nonce, so a conflict can be retried WITHOUT a second Apple sheet. */
  private lastApple: { idToken: string; raw: string } | null = null;

  /** One Apple authorisation -> Firebase credential (fresh nonce each time). */
  private async appleCredential(): Promise<AuthCredential> {
    const { raw, hashed } = await appleNonce();
    const result = await SignInWithApple.authorize({
      clientId: APP_BUNDLE_ID,
      redirectURI: '',
      scopes: 'email name',
      state: '',
      nonce: hashed,
    });
    const idToken = result.response.identityToken?.trim();
    if (!idToken) throw new Error('Apple sign-in did not return an identity token.');
    this.lastApple = { idToken, raw };
    return new OAuthProvider('apple.com').credential({ idToken, rawNonce: raw });
  }

  /** Same Apple token, nonce attached - Firebase accepts it again within its lifetime. */
  private replayApple(): AuthCredential | null {
    return this.lastApple ? new OAuthProvider('apple.com').credential({ idToken: this.lastApple.idToken, rawNonce: this.lastApple.raw }) : null;
  }

  /**
   * Native credential flow: link onto the anonymous uid; existing account wins.
   * @param reissue Apple only: mint a brand-new credential for the conflict
   *   retry. Apple identity tokens are single-use AND the credential Firebase
   *   attaches to the conflict error has lost its nonce, so replaying either
   *   fails with auth/missing-or-invalid-nonce (build 11 field test: the Apple
   *   identity already existed from build 8 under another uid).
   */
  private async linkOrSignInNative(cred: AuthCredential, method: 'google' | 'apple', reissue?: () => Promise<AuthCredential>): Promise<void> {
    const current = this.auth.currentUser;
    try {
      // Link (keeps the device uid and everything under it) only when there is
      // something to keep. A fresh install has nothing, and linking there just
      // risks the conflict below - which for Apple costs a second prompt.
      // Apple: never link. Firebase treats a replayed Apple token as a duplicate
      // credential, so a link that conflicts always costs a SECOND Apple sheet
      // (builds 13-16). Signing straight in is one prompt; the device Garage
      // merges into the account on the next sync anyway.
      if (current?.isAnonymous && hasLocalData() && method !== 'apple') await linkWithCredential(current, cred);
      else await signInWithCredential(this.auth, cred);
    } catch (e) {
      const code = (e as { code?: string }).code ?? '';
      if (code === 'auth/credential-already-in-use' || code === 'auth/email-already-in-use') {
        this.diag.report('native sign-in: existing account, switching', { method, code });
        if (reissue) {
          // Apple: first replay the SAME token with its nonce (no second sheet -
          // the earlier retry failed only because the recovered credential had
          // lost the nonce). Only if Firebase refuses do we ask Apple again.
          const replay = this.replayApple();
          if (replay) {
            try {
              await signInWithCredential(this.auth, replay);
              this.diag.report('native sign-in: switched via token replay', { method });
              await this.auth.authStateReady();
              await this.refreshUserSignals();
              this.logEvent('signin', { method });
              return;
            } catch (e2) {
              this.diag.report('native sign-in: replay refused, re-authorising', { method, code: (e2 as { code?: string }).code ?? '' });
            }
          }
          const t = await this.toastCtrl.create({
            message: 'That Apple ID already has a Vekkle account — confirm once more to switch to it.',
            duration: 3500,
            position: 'top',
          });
          await t.present();
          await signInWithCredential(this.auth, await reissue());
        } else {
          const recovered = GoogleAuthProvider.credentialFromError(e as Parameters<typeof GoogleAuthProvider.credentialFromError>[0]);
          await signInWithCredential(this.auth, recovered ?? cred);
        }
      } else {
        throw e;
      }
    }
    // TalentHub lesson: the credential resolves before the auth state has
    // propagated on the native webview - wait for it before reading currentUser.
    await this.auth.authStateReady();
    await this.refreshUserSignals();
    this.diag.report('native sign-in ok', { method, anonymousBefore: !!current?.isAnonymous, uid: this.auth.currentUser?.uid ?? '' });
    this.logEvent('signin', { method });
  }

  // ─────────────────────────── Email one-time code ───────────────────────────

  /** Address a sign-in code was just sent to ('' = none). */
  readonly codeSentTo = signal('');
  private signInRequestId = '';

  /** Step 1: email a 6-digit code (server mints the underlying sign-in link). */
  async requestEmailCode(email: string): Promise<void> {
    const addr = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(addr)) {
      this.authError.set('That email address doesn’t look right.');
      return;
    }
    this.authBusy.set(true);
    this.authError.set('');
    try {
      this.signInRequestId = crypto.randomUUID();
      const res = await fetch(`${FUNCTIONS_BASE}/requestSignInCode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: addr, requestId: this.signInRequestId }),
      });
      const j = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (!res.ok || !j.ok) throw new Error(j.error || 'Could not send the code — please try again.');
      this.codeSentTo.set(addr);
    } catch (e) {
      this.authError.set((e as Error).message);
      this.diag.report('email code request failed', describeError(e));
    } finally {
      this.authBusy.set(false);
    }
  }

  /** Step 2: swap the code for the sign-in link and redeem it (links onto the anonymous account). */
  async verifyEmailCode(code: string): Promise<void> {
    const addr = this.codeSentTo();
    if (!addr) return;
    this.authBusy.set(true);
    this.authError.set('');
    try {
      const res = await fetch(`${FUNCTIONS_BASE}/verifySignInCode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: addr, requestId: this.signInRequestId, code }),
      });
      const j = (await res.json().catch(() => ({}))) as { signInLink?: string; error?: string };
      if (!res.ok || !j.signInLink) throw new Error(j.error || 'That code didn’t work — please try again.');
      await this.redeemEmailLink(addr, j.signInLink);
      this.logEvent('signin', { method: 'email_code' });
      this.codeSentTo.set('');
    } catch (e) {
      this.authError.set(friendlyAuthError(e));
      this.diag.report('sign-in failed', describeError(e));
    } finally {
      this.authBusy.set(false);
    }
  }

  // ─────────────────────────── Email link (legacy landings) ───────────────────────────
  // Sign-in is a one-time code now; nothing SENDS links any more (the sender would have
  // passed capacitor://localhost as the return URL on native). Old links still complete.

  /** Finish an email-link landing when the address had to be asked for. */
  async completeEmailLink(email: string): Promise<void> {
    this.authBusy.set(true);
    this.authError.set('');
    try {
      await this.redeemEmailLink(email.trim().toLowerCase(), location.href);
      this.pendingEmailLink.set(false);
      this.cleanUrl();
    } catch (e) {
      this.authError.set(friendlyAuthError(e));
      this.diag.report('sign-in failed', describeError(e));
    } finally {
      this.authBusy.set(false);
    }
  }

  private async redeemEmailLink(email: string, href: string): Promise<void> {
    const current = this.auth.currentUser;
    if (current?.isAnonymous) {
      try {
        await linkWithCredential(current, EmailAuthProvider.credentialWithLink(email, href));
      } catch (e) {
        const code = (e as { code?: string }).code ?? '';
        if (code === 'auth/credential-already-in-use' || code === 'auth/email-already-in-use') {
          await signInWithEmailLink(this.auth, email, href); // existing account wins; caches merge on sync
        } else {
          throw e;
        }
      }
    } else {
      await signInWithEmailLink(this.auth, email, href);
    }
    try {
      localStorage.removeItem(EMAIL_LINK_KEY);
    } catch {
      /* ignore */
    }
    this.emailLinkSentTo.set('');
    await this.refreshUserSignals();
  }

  // ─────────────────────────── Bootstrap completion ───────────────────────────

  /** Email-link landings and Google-redirect returns, finished on app start. */
  private async completePendingSignIn(): Promise<boolean> {
    // 1. Email link in the URL?
    try {
      if (isSignInWithEmailLink(this.auth, location.href)) {
        let stored = '';
        try {
          stored = localStorage.getItem(EMAIL_LINK_KEY) ?? '';
        } catch {
          /* ignore */
        }
        if (stored) {
          await this.redeemEmailLink(stored, location.href);
          this.cleanUrl();
          return true;
        }
        // Link opened on a device that didn't request it — ask for the address.
        this.pendingEmailLink.set(true);
        return false;
      }
    } catch (e) {
      console.warn('Email link completion failed:', (e as Error).message);
      this.authError.set(friendlyAuthError(e));
      this.diag.report('sign-in failed', describeError(e));
    }

    // 2. Returning from a Google redirect?
    let cached = '';
    try {
      cached = sessionStorage.getItem(REDIRECT_KEY) ?? '';
      sessionStorage.removeItem(REDIRECT_KEY);
    } catch {
      /* ignore */
    }
    try {
      const result = await getRedirectResult(this.auth);
      if (result?.user) {
        await this.refreshUserSignals();
        return true;
      }
    } catch (e) {
      if (cached) this.authError.set(friendlyAuthError(e));
    }
    // iOS Safari sometimes loses the redirect result but lands the user in
    // IndexedDB anyway (TalentHub finding) — give the state a moment.
    if (cached) {
      const deadline = Date.now() + 3000;
      while (Date.now() < deadline) {
        await this.auth.authStateReady();
        if (this.auth.currentUser && !this.auth.currentUser.isAnonymous) {
          await this.refreshUserSignals();
          return true;
        }
        await new Promise((r) => setTimeout(r, 150));
      }
    }
    return false;
  }

  private cleanUrl(): void {
    try {
      history.replaceState(null, '', location.origin + location.pathname);
    } catch {
      /* ignore */
    }
  }

  // ─────────────────────────── Sign out ───────────────────────────

  /** Server-side account + data deletion, then local reset. */
  async deleteAccount(): Promise<void> {
    const token = await this.idToken();
    if (!token) throw new Error('Sign in first.');
    const res = await fetch(`${FUNCTIONS_BASE}/deleteMyAccount`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: '{}',
    });
    if (!res.ok) {
      const j = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(j.error || 'Account deletion failed — please try again.');
    }
    await this.signOutAndReset();
  }

  /** Sign out, clear this device's caches (no cross-account bleed), restart anonymous. */
  async signOutAndReset(): Promise<void> {
    try {
      for (const k of LOCAL_CACHE_KEYS) localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
    await signOut(this.auth);
    await this.auth.authStateReady(); // TalentHub: don't race the teardown
    location.reload();
  }

  // ─────────────────────────── Quirks & tokens ───────────────────────────

  /** iOS Safari / standalone PWAs can't do OAuth popups — use redirects there. */
  private prefersRedirectAuth(): boolean {
    try {
      const isStandalone =
        window.matchMedia?.('(display-mode: standalone)').matches === true ||
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
      if (isStandalone) return true;
      const ua = navigator.userAgent || '';
      const isIos = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && 'ontouchend' in document);
      const isSafari = /Safari\//.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
      return isIos && isSafari;
    } catch {
      return false;
    }
  }

  /** App Check token for the API layer; null when unavailable. */
  async appCheckToken(): Promise<string | null> {
    if (!this.appCheck) return null;
    try {
      return (await getAppCheckToken(this.appCheck, false)).token;
    } catch {
      return null;
    }
  }

  /** Fresh ID token for the API layer; null when signed out/offline. */
  async idToken(): Promise<string | null> {
    await this.ready;
    try {
      return (await this.auth.currentUser?.getIdToken()) ?? null;
    } catch {
      return null;
    }
  }
}


/** Random nonce pair for Sign in with Apple (raw goes to Firebase, SHA-256 to Apple). */
async function appleNonce(): Promise<{ raw: string; hashed: string }> {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  const raw = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw));
  const hashed = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
  return { raw, hashed };
}


/** Probe: can this browser actually keep localStorage? Private windows lie. */
function storageWorks(): boolean {
  try {
    const k = 'av.storage.probe';
    localStorage.setItem(k, '1');
    localStorage.removeItem(k);
    return true;
  } catch {
    return false;
  }
}

/**
 * The person backed out of the OS sheet — not a failure to shout about.
 * Native plugins report this differently from the web popup: the Google plugin
 * uses SIGN_IN_CANCELED, Apple surfaces ASAuthorizationError 1001 ("canceled")
 * only inside the message text. (Same helper in the diecast app.)
 */
function isCancelledSignIn(e: unknown): boolean {
  const err = e as { code?: string; message?: string };
  const code = err?.code ?? '';
  if (code === 'SIGN_IN_CANCELED' || code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return true;
  return /AuthorizationError error 1001\b/.test(String(err?.message ?? ''));
}

function friendlyAuthError(e: unknown): string {
  const code = (e as { code?: string }).code ?? '';
  if (isCancelledSignIn(e)) return '';
  // ASAuthorizationError 1000 ("unknown") in the wild = no iCloud account on the
  // device, or Apple dismissed the sheet without telling us why.
  if (/AuthorizationError error 1000\b/.test(String((e as { message?: string })?.message ?? ''))) {
    return 'Apple could not complete sign-in. Check you are signed in to iCloud in Settings, or use your email address instead.';
  }
  switch (code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return '';
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in window — try again, or use the email link instead.';
    case 'auth/invalid-action-code':
      return 'That sign-in link has expired or was already used — send a fresh one.';
    case 'auth/network-request-failed':
      return 'No connection — try again when you’re back online.';
    case 'auth/operation-not-allowed':
      return 'This sign-in method isn’t enabled yet.';
    default:
      return code ? `Sign-in failed (${code.replace('auth/', '')}).` : 'Sign-in failed — please try again.';
  }
}

/** Long polling on native (see `db`); an already-started instance (hot reload) is kept as is. */
function nativeFirestore(app: FirebaseApp): Firestore {
  if (!IS_NATIVE) return getFirestore(app);
  try {
    return initializeFirestore(app, { experimentalForceLongPolling: true });
  } catch {
    return getFirestore(app);
  }
}

/** Resolve `fallback` if `p` has not settled within `ms` (never rejects). */
function withTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return new Promise<T>((resolve) => {
    const t = setTimeout(() => resolve(fallback), ms);
    p.then((v) => { clearTimeout(t); resolve(v); }, () => { clearTimeout(t); resolve(fallback); });
  });
}

/** Anything on this device worth carrying onto a signed-in account (Garage entries). */
function hasLocalData(): boolean {
  try {
    const raw = localStorage.getItem('av.garage.v1');
    return !!raw && Array.isArray(JSON.parse(raw)) && (JSON.parse(raw) as unknown[]).length > 0;
  } catch {
    return false;
  }
}
